import React from 'react';

import { washHub } from 'store/slices';

import { days } from 'constants/weekdays';

import { useQuery } from 'react-query';
import { fetchSiteById, toggleSiteArchive } from 'api/site';
import { useUpdateSite } from 'hooks/sites/useUpdateSite';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { successToast } from 'utils/successToast';
import { SiteDetailsProps } from './SiteDetails.props';

import SiteDetailsView from './SiteDetails.view';

const SiteDetailsContainer = (props: SiteDetailsProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const siteId = params.id || '';
  const siteName = location?.state?.name || '';

  const washHubId = washHub.selectors.useSelectedWashHubId();

  const { data: siteData, isLoading } = useQuery(
    [`fetch-sites-${washHubId}`, siteId],
    () => fetchSiteById(siteId),
    { enabled: !!siteId },
  );

  const queryClient = useQueryClient();

  const { onUpdateSite, isEdiLoading } = useUpdateSite(washHubId, () => {
    navigate(-1);
  });

  const { mutate: toggleArchive, isLoading: isTogglingArchive } = useMutation(
    toggleSiteArchive,
    {
      onSuccess: () => {
        successToast('Site archive status updated successfully');
        queryClient.invalidateQueries(`fetch-sites-${washHubId}`);
        queryClient.invalidateQueries(`fetch-sites-all-${washHubId}`);
        queryClient.invalidateQueries(`fetch-sites-active-${washHubId}`);
        queryClient.invalidateQueries(`fetch-sites-inactive-${washHubId}`);
        queryClient.invalidateQueries(`fetch-sites-archived-${washHubId}`);
        queryClient.invalidateQueries([`fetch-sites-${washHubId}`, siteId]);

        queryClient.invalidateQueries(`fetch-sites-${washHubId}-select`, {
          exact: true,
        });
        queryClient.invalidateQueries(
          `fetch-sites-active-${washHubId}-select`,
          {
            exact: true,
          },
        );
        queryClient.invalidateQueries('fetch-sites-select-all', {
          exact: true,
        });
        queryClient.invalidateQueries('fetch-sites-active-select-all', {
          exact: true,
        });
        queryClient.invalidateQueries(['fetch-sites-active']);
      },
    },
  );

  const completeEmptyPayloadFields = (payload: any) => {
    const capacity = Number(payload?.capacity || '0');

    const editPortalName =
      payload?.portal === true ? payload?.portalName : `${siteId}`;

    const portalName = editPortalName;

    /**
     * Because the daily state should have a deliveryDayOfTheWeek sate as every day.
     */
    const deliveryDayOfTheWeek =
      payload?.deliveryFrequency === 'Daily'
        ? (payload.deliveryDayOfTheWeek = days)
        : payload.deliveryDayOfTheWeek;

    const zip = payload?.address?.zip || null;

    payload = {
      ...payload,
      _id: siteId,
      deliveryDayOfTheWeek,
      zip,
      capacity,
      washHub: payload?.washHub || washHubId,
      portal: payload?.portal,
      portalName: portalName,
      id: payload?._id || siteId,
      logo: payload?.logo || '',
      banner: payload?.banner || '',
      invoice: payload?.invoice,
      qrLandingUrl: payload?.qrLandingUrl || '',
    };

    return payload;
  };

  const onSubmit = (payload: any) => {
    delete payload._isUploading;

    const { isArchived, ...restPayload } = payload;
    const sanitizedPayload = completeEmptyPayloadFields(restPayload);

    // Handle archive toggle separately using the toggle endpoint
    const previousIsArchived = siteData?.status === 'archived';
    if (isArchived !== previousIsArchived) {
      toggleArchive(siteId);
    }

    // Check if there are other changes besides archive
    const hasOtherChanges = Object.keys(restPayload).some(
      (key) =>
        key !== 'isArchived' && restPayload[key] !== (siteData as any)?.[key],
    );

    if (hasOtherChanges) {
      onUpdateSite(sanitizedPayload);
    }
  };

  return (
    <SiteDetailsView
      siteId={siteId}
      siteName={siteName}
      site={siteData}
      onSubmit={onSubmit}
      isLoading={isEdiLoading}
      isLoadingSite={isLoading}
    />
  );
};

export default SiteDetailsContainer;
